import React from "react";
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Chip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const DatadogAlertModal = ({ open, onClose, alert }) => {
  if (!alert) return null;

  const priorityColors = {
    1: "#BA1650", // Critical (P1)
    2: "#E97F03", // High (P2)
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyles.container}>
        <Paper sx={modalStyles.paper}>
          {/* Sticky Header */}
          <Box sx={modalStyles.header}>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#F9CA00" }}>
              Datadog Alert Details
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ color: "#fff" }} />
            </IconButton>
          </Box>

          <Divider sx={{ backgroundColor: "#777" }} />

          {/* Scrollable Content */}
          <Box
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              padding: "10px",
              overflowX: "hidden",
            }}
          >
            {/* Alert Info Table */}
            <TableContainer sx={{ backgroundColor: "#1E1E1E" }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>{alert.name}</StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>Type</StyledTableCell>
                    <StyledTableCell>{alert.type}</StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell>
                      <span
                        style={{
                          color: alert.status === "Alert" ? "#BA1650" : "#70C930",
                        }}
                      >
                        {alert.status}
                      </span>
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>Priority</StyledTableCell>
                    <StyledTableCell>
                      <span
                        style={{
                          color: priorityColors[alert.priority] || "#9D419B",
                        }}
                      >
                        P{alert.priority}
                      </span>
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>Classification</StyledTableCell>
                    <StyledTableCell>{alert.classification}</StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>Last Triggered</StyledTableCell>
                    <StyledTableCell>
                      {dayjs.unix(alert.last_triggered_ts).fromNow()}
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Metrics */}
            <Typography variant="subtitle1" sx={modalStyles.sectionTitle}>
              📊 Metrics Tracked
            </Typography>
            <Grid container spacing={1} sx={{ marginLeft: "0px" }}>
              {alert.metrics.map((metric, index) => (
                <Chip key={index} label={metric} sx={modalStyles.chip} />
              ))}
            </Grid>

            {/* Scopes */}
            <Typography variant="subtitle1" sx={modalStyles.sectionTitle}>
              🌍 Affected Scopes
            </Typography>
            <Grid container spacing={1} sx={{ marginLeft: "0px" }}>
              {alert.scopes.map((scope, index) => (
                <Chip key={index} label={scope} sx={modalStyles.chip} />
              ))}
            </Grid>

            {/* Creator Info */}
            <Typography
              variant="subtitle1"
              sx={[modalStyles.sectionTitle, { marginBottom: "5px" }]}
            >
              👤 Created By
            </Typography>
            <TableContainer sx={{ backgroundColor: "#1E1E1E" }}>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>{alert.creator.name}</StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>Email</StyledTableCell>
                    <StyledTableCell>{alert.creator.handle}</StyledTableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            {/* Notifications */}
            <Typography
              variant="subtitle1"
              sx={[modalStyles.sectionTitle, { marginBottom: "5px" }]}
            >
              📬 Notifications Sent To
            </Typography>

            {alert.notifications.length > 0 ? (
              <TableContainer sx={{ backgroundColor: "#1E1E1E" }}>
                <Table size="small">
                  <TableBody>
                    {alert.notifications.map((notif, index) => (
                      <React.Fragment key={index}>
                        <TableRow>
                          <StyledTableCell>Name</StyledTableCell>
                          <StyledTableCell>{notif.name}</StyledTableCell>
                        </TableRow>
                        <TableRow>
                          <StyledTableCell>Email</StyledTableCell>
                          <StyledTableCell>{notif.handle}</StyledTableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography sx={modalStyles.text}>No notifications sent.</Typography>
            )}

            {/* Alert Query */}
            <Typography variant="subtitle1" sx={modalStyles.sectionTitle}>
              🔍 Query
            </Typography>
            <Typography sx={modalStyles.codeBlock}>{alert.query}</Typography>

            {/* Tags */}
            <Typography variant="subtitle1" sx={modalStyles.sectionTitle}>
              🏷️ Tags
            </Typography>
            <Grid container spacing={1} sx={{ marginLeft: "0px" }}>
              {alert.tags.map((tag, index) => (
                <Chip key={index} label={tag} sx={modalStyles.chip} />
              ))}
            </Grid>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

/* Styled Table Cell */
const StyledTableCell = ({ children }) => (
  <TableCell
    sx={{
      fontSize: "14px",
      color: "#eee",
      border: "1px solid rgba(255, 255, 255, 0.2)",
      padding: "8px",
    }}
  >
    {children}
  </TableCell>
);

/* Modal Styles */
const modalStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",

    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
  paper: {
    width: "80%",

    maxWidth: "1200px",
    backgroundColor: "#303030",
    color: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
    maxHeight: "80vh",
    overflowY: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    backgroundColor: "#303030",
    zIndex: 10,
    position: "sticky",
    top: 0,
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "bold",
    mt: 2,
    mb: 2,
    color: "#F9CA00",
  },
  text: {
    fontSize: "14px",
    color: "#eee",
    mb: 1,
  },
  chip: {
    backgroundColor: "#505050",
    color: "#fff",
    marginRight: "5px",
    marginBottom: "5px",
  },
  codeBlock: {
    fontSize: "13px",
    backgroundColor: "#202020",
    padding: "10px",
    borderRadius: "5px",
    color: "#70C930",
    fontFamily: "monospace",
    overflowX: "auto",
    whiteSpace: "pre-wrap",
  },
};

export default DatadogAlertModal;
